import * as React from 'react';

import { LoadStatus } from '@rematter/frontend-lib-api-client';
import {
  Footer,
  Grid,
  Row,
  Toggle,
  ToggleButtonGroup,
  Toolbar,
} from '@rematter/ui-web';
import { useAtom } from 'jotai';
import { LucideLayoutGrid, LucideVideo } from 'lucide-react';

import { tradeRoutePaths as tradeRoutes } from '../../../routes';
import { LoadRecord } from '../../../types/load';
import { useCreateOutboundTicket } from '../hooks/useCreateOutboundTicket';
import { useListKeyboardShortcuts } from '../hooks/useListKeyboardShortcuts';
import { useTicketListFilter } from '../hooks/useTicketListFilter';
import { DirectionToggle } from '../shared/DirectionToggle';
import { TicketGridLayout } from '../shared/TicketGridLayout';
import { TicketListFilters } from '../shared/TicketListFilters';
import { TicketSidebarCard } from '../shared/TicketSidebarCard';

import { OutboundLoadStatusBadge } from './OutboundLoadStatusBadge';

import {
  $mineOnly,
  $viewMode,
} from '~/modules/trade/_state/scaleTicketPro.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LayoutViewMode } from '~/modules/trade/types/scaleTickets';
import { useIdentity } from '~/shared/hooks/useIdentity';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const getLoadImageFiles = (load: LoadRecord) =>
  (load.files ?? [])
    .filter((f) => f.mimeType.startsWith('image/'))
    .map((f) => ({
      id: f.id,
      fileName: f.fileName,
      mimeType: f.mimeType,
      path: f.downloadUrl,
    }));

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET GRID ⚛️ ⚛️ ⚛️
//
interface OutboundTicketGridProps {
  loads: LoadRecord[];
  onSelectTicket: (id: string) => void;
}

const OutboundTicketGrid = ({
  loads,
  onSelectTicket,
}: OutboundTicketGridProps) => {
  const t = useTranslations();
  return (
    <Grid gap4 className="grid-cols-[repeat(auto-fill,minmax(280px,1fr))]">
      {loads.map((load) => (
        <TicketSidebarCard
          key={load.id}
          id={load.id}
          documentNumber={load.loadNumber}
          contactType={t.scaleTicketPro.customer}
          contactName={load.customerName}
          subtitle={load.salesOrderNumber || ''}
          notesPreview={load.internalNotes || ''}
          lineItemCount={load.lineItemCount ?? load.lineItems?.length ?? 0}
          date={load.timestamps.createdAt}
          isSelected={false}
          onSelect={onSelectTicket}
          imageFiles={getLoadImageFiles(load)}
          statusBadge={
            <OutboundLoadStatusBadge status={load.status as LoadStatus} />
          }
        />
      ))}
    </Grid>
  );
};

//
// ⚛️ ⚛️ ⚛️ VIEW MODE TOGGLE ⚛️ ⚛️ ⚛️
//
interface ViewModeToggleProps {
  viewMode: LayoutViewMode;
  onSelectionChange: (keys: Set<React.Key>) => void;
}

const ViewModeToggle = ({
  viewMode,
  onSelectionChange,
}: ViewModeToggleProps) => (
  <Row gap2 yCenter className="@ViewModeToggle ml-auto">
    <ToggleButtonGroup
      selectionMode="single"
      selectedKeys={new Set([viewMode])}
      onSelectionChange={onSelectionChange}
    >
      <Toggle size="xl" id="grid">
        <LucideLayoutGrid className="h-4 w-4" />
      </Toggle>
      <Toggle size="xl" id="camera">
        <LucideVideo className="h-4 w-4" />
      </Toggle>
    </ToggleButtonGroup>
  </Row>
);

//
// ⚛️ ⚛️ ⚛️ OUTBOUND TICKET LIST ⚛️ ⚛️ ⚛️
//
export const OutboundTicketList = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Atoms
  const [mineOnly, setMineOnly] = useAtom($mineOnly);
  const [viewMode, setViewMode] = useAtom($viewMode);

  // Data hooks
  const { user } = useIdentity();
  const currentUserId = user?.id;

  const {
    items: filteredItems,
    filter,
    setFilter,
    handleSearchChange,
    handleStatusChange,
    handleStatusToggle,
    handleAccountToggle,
    handleClearAccounts,
    loadingState,
    refetch,
  } = useTicketListFilter({ direction: 'outbound', mineOnly, currentUserId });

  const filteredLoads = filteredItems as LoadRecord[];

  const { createTicket, isCreating } = useCreateOutboundTicket();

  // Memos
  const hasActiveFilters = React.useMemo(
    () =>
      filter.searchTerm !== '' ||
      filter.statuses.length > 0 ||
      filter.accountIds.length > 0,
    [filter.searchTerm, filter.statuses.length, filter.accountIds.length],
  );

  // Callbacks
  const handleClearSearch = React.useCallback(() => {
    setFilter((prev) => ({
      ...prev,
      statuses: [],
      searchTerm: '',
      accountIds: [],
    }));
  }, [setFilter]);

  const handleCreateNew = React.useCallback(async () => {
    await createTicket();
  }, [createTicket]);

  const handleSelectTicket = React.useCallback(
    (id: string) => {
      navigate(tradeRoutes.scaleTicketDetail.replace(':id', id));
    },
    [navigate],
  );

  const handleRefresh = React.useCallback(() => {
    refetch?.();
  }, [refetch]);

  const handleViewModeChange = React.useCallback(
    (keys: Set<React.Key>) => {
      const first = [...keys][0];
      if (first) {
        setViewMode(first as LayoutViewMode);
      }
    },
    [setViewMode],
  );

  useListKeyboardShortcuts({
    searchContainerSelector: '.\\@OutboundTicketList',
    onCreateNew: handleCreateNew,
    createModifier: 'alt',
  });

  return (
    <div className="@OutboundTicketList flex h-full flex-col">
      <Toolbar className="flex-wrap gap-2 border-b px-4 py-2">
        <DirectionToggle />

        <div className="h-6 w-px bg-foreground/10" />

        <TicketListFilters
          layout="list"
          direction="outbound"
          filter={filter}
          onSearchChange={handleSearchChange}
          onStatusChange={handleStatusChange}
          onStatusToggle={handleStatusToggle}
          onAccountToggle={handleAccountToggle}
          onClearAccounts={handleClearAccounts}
          mineOnly={mineOnly}
          onMineOnlyChange={setMineOnly}
          onCreateNew={handleCreateNew}
          isCreating={isCreating}
          onRefresh={handleRefresh}
        />

        <ViewModeToggle
          viewMode={viewMode}
          onSelectionChange={handleViewModeChange}
        />
      </Toolbar>

      <TicketGridLayout
        isLoading={!!loadingState?.isInitialLoading}
        ticketsLength={filteredLoads?.length}
        viewMode={viewMode}
        onCreateNew={handleCreateNew}
        isCreating={isCreating}
        hasActiveFilters={hasActiveFilters}
        onClearSearch={handleClearSearch}
      >
        <OutboundTicketGrid
          loads={filteredLoads}
          onSelectTicket={handleSelectTicket}
        />
      </TicketGridLayout>

      <Footer className="border-t px-4 py-2">
        <div className="text-sm text-foreground/50">
          {filteredLoads.length} {t.scaleTicketPro.loads}
        </div>
      </Footer>
    </div>
  );
};
